import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, ResourceRef, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { CodeListItem } from '@models/codelist';
import { CustomerListItem, CustomerListParams, CustomerListResponse } from '@models/customers';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { ConfirmationService } from '@services/confirmation.service';
import { CustomersService } from '@services/customers.service';
import { LocaleSwitcherService } from '@services/locale-switcher.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { BoolComponent } from '@shared/components/bool/bool.component';
import { loadVisibleColumns, saveVisibleColumns } from '@utils/visible-columns-storage';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, map, skip } from 'rxjs';

// ── Column definition types ──────────────────────────────────

type FilterType = 'text' | 'dropdown' | 'boolean' | 'daterange' | 'number' | 'none';
type DropdownSource = 'sites' | 'genders' | 'statuses' | 'countries';
interface ColumnDef {
  key: string;
  labelKey: string;
  filterType: FilterType;
  filterField?: string;
  filterFieldFrom?: string;
  filterFieldTo?: string;
  dropdownSource?: DropdownSource;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'id', labelKey: 'customers.col.id', filterType: 'number', filterField: 'id' },
  {
    key: 'firstName',
    labelKey: 'customers.col.firstName',
    filterType: 'text',
    filterField: 'firstName',
  },
  { key: 'lastName', labelKey: 'customers.col.lastName', filterType: 'text', filterField: 'lastName' },
  { key: 'email', labelKey: 'customers.col.email', filterType: 'text', filterField: 'email' },
  {
    key: 'genderName',
    labelKey: 'customers.col.gender',
    filterType: 'dropdown',
    filterField: 'genderId',
    dropdownSource: 'genders',
  },
  { key: 'address', labelKey: 'customers.col.address', filterType: 'text', filterField: 'address' },
  {
    key: 'postalCode',
    labelKey: 'customers.col.postalCode',
    filterType: 'text',
    filterField: 'postalCode',
  },
  { key: 'city', labelKey: 'customers.col.city', filterType: 'text', filterField: 'city' },
  {
    key: 'countryName',
    labelKey: 'customers.col.country',
    filterType: 'dropdown',
    filterField: 'countryId',
    dropdownSource: 'countries',
  },
  {
    key: 'phoneNumber',
    labelKey: 'customers.col.phoneNumber',
    filterType: 'text',
    filterField: 'phoneNumber',
  },
  {
    key: 'birthDate',
    labelKey: 'customers.col.birthDate',
    filterType: 'daterange',
    filterFieldFrom: 'birthDateFrom',
    filterFieldTo: 'birthDateTo',
  },
  {
    key: 'createdOn',
    labelKey: 'customers.col.createdOn',
    filterType: 'daterange',
    filterFieldFrom: 'createdFrom',
    filterFieldTo: 'createdTo',
  },
  {
    key: 'statusName',
    labelKey: 'customers.col.status',
    filterType: 'dropdown',
    filterField: 'statusId',
    dropdownSource: 'statuses',
  },
  {
    key: 'siteName',
    labelKey: 'customers.col.site',
    filterType: 'dropdown',
    filterField: 'siteId',
    dropdownSource: 'sites',
  },
  {
    key: 'hasAppAccount',
    labelKey: 'customers.col.hasAppAccount',
    filterType: 'boolean',
    filterField: 'hasAppAccount',
  },
];

const DEFAULT_VISIBLE_COLUMNS = ['firstName', 'lastName', 'email', 'phoneNumber'];
const COLUMNS_STORAGE_KEY = 'customer-list-columns';

// Status name → badge CSS classes. Keys must match the Name values from the
// customer statuses codelist (api/codelists/statuses).
const STATUS_CLASSES: Record<string, string> = {
  Suspect: 'bg-blue-100 text-blue-800',
  Prospect: 'bg-yellow-100 text-yellow-800',
  Opportunity: 'bg-orange-100 text-orange-800',
  Customer: 'bg-green-100 text-green-800',
  Owner: 'bg-purple-100 text-purple-800',
};

// ── Component ────────────────────────────────────────────────

@Component({
  selector: 'app-customer-list',
  templateUrl: './customer-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslatePipe,
    BoolComponent,
  ],
})
export class CustomerListComponent {
  private readonly customersService = inject(CustomersService);
  readonly codeListsService = inject(CodeListsService);
  private readonly localeSwitcher = inject(LocaleSwitcherService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  // Reference data — sourced from cached CodeListsService signals
  readonly dropdownSources: Record<DropdownSource, () => CodeListItem[]> = {
    sites: this.codeListsService.sites,
    genders: this.codeListsService.genders,
    statuses: this.codeListsService.statuses,
    countries: this.codeListsService.countries,
  };

  readonly allColumns = ALL_COLUMNS;

  // Column visibility — synced to URL query param `columns`, falls back to localStorage.
  readonly visibleColumnKeys = signal<string[]>(
    loadVisibleColumns(
      COLUMNS_STORAGE_KEY,
      this.route.snapshot.queryParamMap.get('columns'),
      ALL_COLUMNS.map((c) => c.key),
      DEFAULT_VISIBLE_COLUMNS,
    ),
  );
  readonly displayedColumns = computed(() => [...this.visibleColumnKeys(), 'actions']);
  readonly filterColumns = computed(() => this.displayedColumns().map((k) => `filter-${k}`));

  readonly page = signal(1);
  readonly pageSize = signal(25);

  // Global search input (raw) and debounced query
  readonly searchInput = signal('');
  private readonly searchQuery = signal('');

  // Per-column text filters: raw inputs → debounced values
  private readonly textFilterInputs = signal<Record<string, string>>({});
  private readonly textFilters = signal<Record<string, string>>({});

  // Per-column dropdown / boolean filters (applied immediately)
  readonly dropdownFilters = signal<Record<string, number | boolean | null>>({});

  // Per-column numeric / date-range filters. Holds the `id` exact value and the date
  // from/to fields as ISO yyyy-MM-dd strings. Date pickers apply on selection; the
  // numeric `id` input applies on commit (blur/Enter) so we don't fire a request for
  // every partial value typed (e.g. 1 → 12 → 123).
  readonly rangeFilters = signal<Record<string, string | number | null>>({});

  // Computed request — built dynamically from column definitions
  private readonly requestParams = computed<CustomerListParams>(() => {
    const tf = this.textFilters();
    const df = this.dropdownFilters();
    const rf = this.rangeFilters();
    const entries: [string, unknown][] = [];
    for (const col of ALL_COLUMNS) {
      if (col.filterType === 'text' && col.filterField) {
        entries.push([col.filterField, tf[col.filterField] || null]);
      } else if ((col.filterType === 'dropdown' || col.filterType === 'boolean') && col.filterField) {
        entries.push([col.filterField, df[col.filterField] ?? null]);
      } else if (col.filterType === 'number' && col.filterField) {
        entries.push([col.filterField, rf[col.filterField] ?? null]);
      } else if (col.filterType === 'daterange' && col.filterFieldFrom && col.filterFieldTo) {
        entries.push([col.filterFieldFrom, rf[col.filterFieldFrom] ?? null]);
        entries.push([col.filterFieldTo, rf[col.filterFieldTo] ?? null]);
      }
    }
    return {
      search: this.searchQuery() || null,
      page: this.page(),
      pageSize: this.pageSize(),
      locale: this.localeSwitcher.selectedLocaleCode(),
      ...Object.fromEntries(entries),
    } as CustomerListParams;
  });

  // Customers — re-fetches whenever requestParams changes
  readonly customersResource: ResourceRef<CustomerListResponse | undefined> = rxResource({
    params: () => this.requestParams(),
    stream: ({ params }) =>
      this.customersService.getCustomers(params).pipe(showGenericErrorToast(this.toastService, () => undefined)),
  });

  // Keep previous data visible while a new request is loading — filters out the
  // `undefined` that rxResource emits when it clears state between fetches.
  readonly customers = toSignal(
    toObservable(this.customersResource.value).pipe(
      filter((v): v is CustomerListResponse => v !== undefined),
      map((v) => v.items),
    ),
    { initialValue: [] as CustomerListItem[] },
  );
  readonly totalCount = toSignal(
    toObservable(this.customersResource.value).pipe(
      filter((v): v is CustomerListResponse => v !== undefined),
      map((v) => v.totalCount),
    ),
    { initialValue: 0 },
  );

  constructor() {
    this.syncQueryParams();

    // Debounce global search
    toObservable(this.searchInput)
      .pipe(skip(1), debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((v) => {
        this.searchQuery.set(v);
        this.page.set(1);
      });

    // Debounce per-column text filters
    toObservable(this.textFilterInputs)
      .pipe(skip(1), debounceTime(300), takeUntilDestroyed())
      .subscribe((v) => {
        this.textFilters.set(v);
        this.page.set(1);
      });
  }

  // ── Event handlers ────────────────────────────────────────

  onSearchInput(value: string): void {
    this.searchInput.set(value);
  }

  onTextFilterInput(filterField: string, value: string): void {
    this.textFilterInputs.update((prev) => ({ ...prev, [filterField]: value }));
  }

  onDropdownFilterChange(filterField: string, value: number | boolean | ''): void {
    this.dropdownFilters.update((prev) => ({ ...prev, [filterField]: value === '' ? null : value }));
    this.page.set(1);
  }

  onRangeFilterChange(field: string, value: string, isNumber: boolean): void {
    let parsed: string | number | null;
    if (value === '') {
      parsed = null;
    } else if (isNumber) {
      const num = Number(value);
      parsed = Number.isFinite(num) ? num : null; // ignore partial/invalid input (NaN)
    } else {
      parsed = value;
    }
    this.rangeFilters.update((prev) => ({ ...prev, [field]: parsed }));
    this.page.set(1);
  }

  // Date filters use the Material datepicker (Date in, ISO yyyy-MM-dd string stored for the query param).
  onDateFilterChange(field: string, date: Date | null): void {
    const iso = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : null;
    this.rangeFilters.update((prev) => ({ ...prev, [field]: iso }));
    this.page.set(1);
  }

  getDateFilterValue(field: string): Date | null {
    const v = this.rangeFilters()[field];
    if (typeof v !== 'string' || !v) return null;
    // Parse the yyyy-MM-dd string as a LOCAL date; new Date('yyyy-MM-dd') would parse as UTC and
    // can shift the day in non-UTC timezones.
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  getRangeFilterValue(field: string): string {
    const v = this.rangeFilters()[field];
    return v == null ? '' : String(v);
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex + 1);
    this.pageSize.set(event.pageSize);
  }

  // ── Column helpers ────────────────────────────────────────

  toggleColumn(key: string): void {
    const current = this.visibleColumnKeys();
    const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    if (updated.length === 0) return;
    const ordered = ALL_COLUMNS.map((c) => c.key).filter((k) => updated.includes(k));
    this.visibleColumnKeys.set(ordered);
    saveVisibleColumns(COLUMNS_STORAGE_KEY, ordered);
    this.syncQueryParams();
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      queryParams: {
        columns: this.visibleColumnKeys().join(','),
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  isColumnVisible(key: string): boolean {
    return this.visibleColumnKeys().includes(key);
  }

  getDropdownFilterValue(filterField: string): number | '' {
    const v = this.dropdownFilters()[filterField];
    return v == null ? '' : (v as number);
  }

  getBooleanFilterValue(filterField: string): boolean | '' {
    const v = this.dropdownFilters()[filterField];
    return v == null ? '' : (v as boolean);
  }

  getTextFilterValue(filterField: string): string {
    return this.textFilterInputs()[filterField] ?? '';
  }

  // ── Status styling ────────────────────────────────────────

  getStatusClass(statusId: number): string {
    const name = this.codeListsService.statusMap().get(statusId) ?? '';
    return STATUS_CLASSES[name] ?? 'bg-gray-100 text-gray-800';
  }

  // ── Row actions ───────────────────────────────────────────

  navigateToCreate(): void {
    this.router.navigate(['/customers/create'], {
      queryParams: { backUrl: this.router.url },
    });
  }

  navigateToCustomer(id: number): void {
    this.router.navigate(['/customers/edit', id], {
      queryParams: { backUrl: this.router.url },
    });
  }

  async deleteCustomer(customer: CustomerListItem, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || `#${customer.id}`;
    const confirmed = await this.confirmation.open({
      title: this.translateService.instant('customers.confirm.deletion-title'),
      prompt: this.translateService.instant('customers.confirm.deletion-prompt', { fullName }),
    });
    if (!confirmed) return;
    await this.deleteCustomerConfirmed(customer.id);
  }

  private async deleteCustomerConfirmed(id: number): Promise<void> {
    try {
      await firstValueFrom(this.customersService.deleteCustomer(id));
      this.toastService.showSuccess(this.translateService.instant('customers.toast.delete-success'));
      this.customersResource.reload();
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 400) {
        this.toastService.showError(this.translateService.instant('customers.toast.delete-has-reservations'));
      } else {
        this.toastService.showError(this.translateService.instant('customers.toast.delete-failed'));
      }
    }
  }
}
